/**
 * Explosions (docs/spec/04 §1), generated: a starburst that opens, hollows and thins, written out
 * as ordinary sprite frames so it goes through the same atlas as the drawn art.
 */

import { BLUE, CYAN, ORANGE, RED, SKY, WHITE, YELLOW } from '../palette';
import type { SpriteDef } from './types';

const SIZE = 32;

/** A fixed scatter: the same explosion every run, and no two spokes alike. */
function hash(a: number, b: number): number {
  let h = (Math.imul(a + 1, 0x9e3779b1) ^ Math.imul(b + 7, 0x85ebca6b)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
  return ((h ^ (h >>> 13)) >>> 0) / 4294967296;
}

interface BoomLook {
  readonly frames: number;
  /** Outer radius of the last frame. */
  readonly reach: number;
  readonly spokes: number;
  /** Characters from the hot centre outward. */
  readonly ramp: string;
  readonly seed: number;
}

function frame(look: BoomLook, f: number): string[] {
  const p = (f + 1) / look.frames;
  const outer = 4 + (look.reach - 4) * Math.sqrt(p);
  // The middle burns out: a ball at first, a ring of fragments at the end.
  const inner = p < 0.45 ? 0 : outer * (p - 0.3) * 0.95;
  const rows: string[] = [];
  for (let y = 0; y < SIZE; y++) {
    let row = '';
    for (let x = 0; x < SIZE; x++) {
      const dx = x + 0.5 - SIZE / 2;
      const dy = y + 0.5 - SIZE / 2;
      const d = Math.hypot(dx, dy);
      const spoke = Math.floor(((Math.atan2(dy, dx) + Math.PI) / (Math.PI * 2)) * look.spokes) % look.spokes;
      // Every other spoke is long: a star, not a disc.
      const length = outer * (spoke % 2 === 0 ? 1 : 0.62 + 0.2 * hash(spoke, look.seed));
      const gap = hash(spoke, look.seed + 31) < p * 0.35;
      const speck = hash(x * 37 + y, look.seed + f) < p * p * 0.5;
      let ch = '.';
      if (d <= length && d >= inner && !(gap && d > outer * 0.4) && !speck) {
        const heat = (d - inner) / Math.max(1, length - inner) * 0.75 + p * 0.55;
        const i = Math.min(look.ramp.length - 1, Math.floor(heat * (look.ramp.length - 1)));
        ch = look.ramp[i] ?? '.';
      }
      row += ch;
    }
    rows.push(row);
  }
  return rows;
}

function boom(look: BoomLook, colours: Readonly<Record<string, number>>): SpriteDef {
  return { frames: Array.from({ length: look.frames }, (_, f) => frame(look, f)), colours };
}

/** An enemy: white to yellow to orange to red, 5 frames. */
export const BOOM: SpriteDef = boom(
  { frames: 5, reach: 13, spokes: 12, ramp: 'WWYYOORR', seed: 3 },
  { W: WHITE, Y: YELLOW, O: ORANGE, R: RED },
);

/** The Lancer: bigger, in its own colours, 6 frames. */
export const BIG_BOOM: SpriteDef = boom(
  { frames: 6, reach: 15.5, spokes: 16, ramp: 'WWWCCSSRRB', seed: 11 },
  { W: WHITE, C: CYAN, S: SKY, R: RED, B: BLUE },
);
